import React from 'react';
import { StatusManager } from 'common/components/StatusManager';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';
import { nestTernary } from 'UI/utils';

import useCandidateDataSheet from './hooks/useCandidateDataSheet';
import {
  /**
   * @typedef {Object} CandidateDataSheetDrawerProps
   * @property {string} drawerTitle
   * @property {Object} draftData
   * @property {string} candidateDataSheetId
   * @property {Function} onClose
   * @property {Function} onSaveCandidateDataSheet
   */
  StyledGpacGeneratorForm,
  useDataSheetDrawerStyles
} from './CandidateDataSheetDrawer.styled';

/**
 * @param {CandidateDataSheetDrawerProps} props
 */
const CandidateDataSheetDrawer = props => {
  const { onClose, drawerTitle, draftData, candidateDataSheetId, onSaveCandidateDataSheet } = props;

  const classes = useDataSheetDrawerStyles();
  const [dataSheetState, dataSheetActions] = useCandidateDataSheet({
    candidateDataSheetId,
    draftData,
    onSaveCandidateDataSheet,
    onClose
  });

  const isLoading = dataSheetState.isFetching || dataSheetState.isFetchingCandidateDataSheetData;
  const isError = !!(dataSheetState.error || dataSheetState.errorCandidateDataSheetData);
  const status = nestTernary(
    isLoading,
    UIStatus.Loading,
    nestTernary(isError, UIStatus.Error, UIStatus.Success)
  );

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      }
    },
    {
      props: {
        initialText: dataSheetState.primaryButtonName,
        variant: 'contained',
        onClick: dataSheetActions.onSaveClick,
        onProgressText: strings.shared.ui.saving,
        isSaving: dataSheetState.isSaving,
        disabled: dataSheetState.isSaving
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: drawerTitle }}
      onClose={onClose}
      FPDrawerProps={{ open: true, classes: { paper: classes.drawerLarge } }}
      hideFooter={status !== UIStatus.Success}
      footer={
        status === UIStatus.Success && (
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        )
      }
    >
      <StatusManager status={status}>
        {!!dataSheetState.data && (
          <StyledGpacGeneratorForm
            schema={dataSheetState.data.structure}
            uiSchema={dataSheetState.data.ui}
            httpOptions={dataSheetState.httpOptions}
            showErrorList={false}
            formData={dataSheetState.formData}
            onSubmit={dataSheetActions.save}
            onChange={dataSheetActions.handleOnChange}
            submitRef={dataSheetState.saveButtonRef}
            baseUrl={dataSheetState.baseUrl}
            secondaryBaseUrl={dataSheetState.secondaryBaseUrl}
            noHtml5Validate
            focusOnFirstError
          />
        )}
      </StatusManager>
    </FPDrawer>
  );
};

export default CandidateDataSheetDrawer;
